<script setup>
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import {
    NCard,
    NInputNumber,
    NSteps,
    NStep,
    NForm,
    NFormItem,
    NInput,
    NButton,
    NSelect,
    NSpace,
    NTag,
    NDynamicInput,
    NResult,
    NPageHeader,
    NSwitch,
    NTimePicker,
    NDatePicker,
    NCheckbox,
    NCheckboxGroup,
    NRadioGroup,
    NRadio,
    NUpload,
    NAvatar,
  } from "naive-ui";

  // API utilities
  import { 
    API_BASE_URL, 
    saveProvider as saveProviderService,
    formatProviderDataForForm,
    formatFormDataForApi,
    uploadProviderProfileImage,
    createImagePreview,
    cleanupImagePreview
  } from "@/services/provider";
  import { authToken, isAuthenticated, authFetch } from "@/services/auth";

  const route = useRoute();
  const router = useRouter();

  // Determine if we're editing (id in URL) or creating new
  const isEditMode = computed(() => !!route.params.id);
  const pageTitle = computed(() => isEditMode.value ? "Edit Legal Provider" : "Add New Legal Provider");

  // Image upload states
  const uploading = ref(false);
  const uploadError = ref('');
  const previewUrl = ref('');
  const selectedFile = ref(null);

  // Form data with all legal fields
  const formData = ref({
    // Basic Information
    provider_name: "",
    provider_type: null,
    specializations: [], // legal practice areas
    tags: [],
    additional_info: "",
    is_draft: false,
    profile_image: "",
    
    // Address Details
    address_line1: "",
    address_line2: "",
    address_line3: "",
    city: "",
    state: "",
    postal_code: "", // ✅ Fixed: Changed from 0 to ""
    additional_locations: [],
    
    // Contact Information
    phone_numbers: [],
    website_url: "",
    social_media: [],
    
    // Professional Details
    services: [], // legal services offered
    client_types: [], // individuals, businesses, nonprofits, etc.
    spoken_languages: [],
    education: [], // law schools, degrees
    certifications: [], // bar admissions, specializations
    years_of_experience: null,
    
    // Practice Information
    accepts_new_clients: null,
    virtual_consultations: null,
    office_hours: [],
    parking_info: "",
    payment_methods: [],
    accessibility_options: [],
    average_response_time_minutes: null,
    
    // Legal Practice Specific Fields
    provider_staff: [], // attorneys, paralegals, staff
    affiliated_organizations: [], // bar associations, legal groups
    services_offered: [], // detailed legal services
    technology_available: [], // case management systems, e-filing, etc.
    awards_accreditations: [],
    court_representations: [], // District Court, High Court, Supreme Court, etc.
    pro_bono_services: [], // free legal aid services offered
    case_types_handled: [], // Criminal, Civil, Corporate, Family, Tax, etc.
  });

  const loading = ref(false);
  const saving = ref(false);
  const success = ref(false);
  const error = ref(null);

  // Expanded steps to include legal-specific sections
  const currentStep = ref(1);
  const totalSteps = 7; // Updated to 7 steps

  const stepTitles = [
    "Basic Information",
    "Address Details", 
    "Contact Info",
    "Professional Details",
    "Practice Information",
    "Legal Practice Details",
    "Review & Submit"
  ];

  // Image upload handling
  const handleImageUpload = async (options) => {
    const { file } = options;
    if (!file) return;

    uploadError.value = '';
    selectedFile.value = file.file;
    
    // Create preview
    cleanupImagePreview(previewUrl.value); // Clean up previous preview
    previewUrl.value = createImagePreview(file.file);

    // Upload image
    await uploadImage(file.file);
  };

  const uploadImage = async (file) => {
    if (!file) return;

    uploading.value = true;
    uploadError.value = '';

    try {
      // Custom processing options
      const processingOptions = {
        maxWidth: 1200,  // Limit width
        maxHeight: 1200, // Limit height
        quality: 0.8,    // JPEG quality
        format: 'image/jpeg' // Force JPEG format
      };

      const result = await uploadProviderProfileImage(
        authToken.value, 
        file, 
        processingOptions
      );

      if (result.success) {
        console.log('Upload successful:', result.image_url);
        formData.value.profile_image = result.image_url;
      } else {
        uploadError.value = result.error || 'Failed to upload image';
      }
    } catch (error) {
      uploadError.value = error.message || 'Failed to upload image';
    } finally {
      uploading.value = false;
    }
  };

  const clearPreview = () => {
    cleanupImagePreview(previewUrl.value);
    previewUrl.value = '';
    selectedFile.value = null;
    uploadError.value = '';
    formData.value.profile_image = '';
  };

  // Fetch provider data if in edit mode
  onMounted(async () => {
    if (isEditMode.value) {
      try {
        loading.value = true;
        
        if (!isAuthenticated.value) {
          throw new Error("Authentication required");
        }
        
        const response = await authFetch(`${API_BASE_URL}/providers/${route.params.id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const providerData = await response.json();
        const formattedData = formatProviderDataForForm(providerData);
        formData.value = formattedData;

        // Set preview URL if profile image exists
        if (formattedData.profile_image) {
          previewUrl.value = formattedData.profile_image;
        }
        
      } catch (err) {
        error.value = "Failed to load provider data";
        console.error(err);
      } finally {
        loading.value = false;
      }
    }
  });

  // Cleanup on component unmount
  onUnmounted(() => {
    if (previewUrl.value && previewUrl.value.startsWith('blob:')) {
      cleanupImagePreview(previewUrl.value);
    }
  });

  // Move to next step
  const nextStep = () => {
    if (currentStep.value < totalSteps) {
      currentStep.value++;
      window.scrollTo(0, 0);
    }
  };

  // Move to previous step
  const prevStep = () => {
    if (currentStep.value > 1) {
      currentStep.value--;
      window.scrollTo(0, 0);
    }
  };

  // Save provider using the service
  const saveProvider = async () => {
    saving.value = true;
    error.value = null;

    try {    
      if (!isAuthenticated.value) {
        throw new Error("Authentication required");
      }

      const payload = { ...formData.value };

      console.log('=== SAVING PROVIDER ===');
      console.log('additional_locations:', payload.additional_locations);
      console.log('Full payload:', payload);
      
      if (isEditMode.value) {
        payload.id = route.params.id;
      }
      
      const savedProvider = await saveProviderService(authToken.value, payload);
      success.value = true;
      
      if (!isEditMode.value) {
        // Reset form for new provider
        Object.keys(formData.value).forEach(key => {
          if (Array.isArray(formData.value[key])) {
            formData.value[key] = [];
          } else if (typeof formData.value[key] === 'string') {
            formData.value[key] = '';
          } else if (typeof formData.value[key] === 'number') {
            formData.value[key] = null;
          } else if (typeof formData.value[key] === 'boolean') {
            formData.value[key] = false;
          } else {
            formData.value[key] = null;
          }
        });
        
        // Ensure postal_code remains a string after reset
        formData.value.postal_code = "";
        
        // Clear image upload states
        clearPreview();
      }
      
      setTimeout(() => {
        router.push('/provider-profile');
      }, 2000);
      
    } catch (err) {
      console.error('=== SAVE ERROR ===');
      console.error('Error object:', err);
      error.value = err.message || "Failed to save provider";
    } finally {
      saving.value = false;
    }
  };

  // Legal Practice Areas (Indian Legal System)
  const specializationOptions = [
    { label: "Civil Law", value: "civil_law" },
    { label: "Criminal Law", value: "criminal_law" },
    { label: "Corporate Law", value: "corporate_law" },
    { label: "Family Law", value: "family_law" },
    { label: "Property Law", value: "property_law" },
    { label: "Labor Law", value: "labor_law" },
    { label: "Tax Law", value: "tax_law" },
    { label: "Constitutional Law", value: "constitutional_law" },
    { label: "Consumer Protection", value: "consumer_protection" },
    { label: "Intellectual Property Law", value: "intellectual_property" },
    { label: "Banking Law", value: "banking_law" },
    { label: "Insurance Law", value: "insurance_law" },
    { label: "Environmental Law", value: "environmental_law" },
    { label: "Immigration Law", value: "immigration_law" },
    { label: "Cyber Law", value: "cyber_law" },
    { label: "Real Estate Law", value: "real_estate_law" },
    { label: "Securities Law", value: "securities_law" },
    { label: "International Law", value: "international_law" },
    { label: "Administrative Law", value: "administrative_law" },
    { label: "Human Rights Law", value: "human_rights_law" },
    { label: "Arbitration & Mediation", value: "arbitration_mediation" },
    { label: "Competition Law", value: "competition_law" },
    { label: "Contract Law", value: "contract_law" },
    { label: "Tort Law", value: "tort_law" },
    { label: "Commercial Law", value: "commercial_law" },
    { label: "Maritime Law", value: "maritime_law" },
    { label: "Aviation Law", value: "aviation_law" },
    { label: "Sports Law", value: "sports_law" },
    { label: "Entertainment Law", value: "entertainment_law" },
    { label: "Agriculture Law", value: "agriculture_law" }
  ];

  const stateOptions = [
    { label: "Andhra Pradesh", value: "Andhra Pradesh" },
    { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
    { label: "Assam", value: "Assam" },
    { label: "Bihar", value: "Bihar" },
    { label: "Chhattisgarh", value: "Chhattisgarh" },
    { label: "Goa", value: "Goa" },
    { label: "Gujarat", value: "Gujarat" },
    { label: "Haryana", value: "Haryana" },
    { label: "Himachal Pradesh", value: "Himachal Pradesh" },
    { label: "Jharkhand", value: "Jharkhand" },
    { label: "Karnataka", value: "Karnataka" },
    { label: "Kerala", value: "Kerala" },
    { label: "Madhya Pradesh", value: "Madhya Pradesh" },
    { label: "Maharashtra", value: "Maharashtra" },
    { label: "Manipur", value: "Manipur" },
    { label: "Meghalaya", value: "Meghalaya" },
    { label: "Mizoram", value: "Mizoram" },
    { label: "Nagaland", value: "Nagaland" },
    { label: "Odisha", value: "Odisha" },
    { label: "Punjab", value: "Punjab" },
    { label: "Rajasthan", value: "Rajasthan" },
    { label: "Sikkim", value: "Sikkim" },
    { label: "Tamil Nadu", value: "Tamil Nadu" },
    { label: "Telangana", value: "Telangana" },
    { label: "Tripura", value: "Tripura" },
    { label: "Uttar Pradesh", value: "Uttar Pradesh" },
    { label: "Uttarakhand", value: "Uttarakhand" },
    { label: "West Bengal", value: "West Bengal" },
    { label: "Andaman and Nicobar Islands", value: "Andaman and Nicobar Islands" },
    { label: "Chandigarh", value: "Chandigarh" },
    { label: "Dadra and Nagar Haveli and Daman and Diu", value: "Dadra and Nagar Haveli and Daman and Diu" },
    { label: "Delhi", value: "Delhi" },
    { label: "Jammu and Kashmir", value: "Jammu and Kashmir" },
    { label: "Ladakh", value: "Ladakh" },
    { label: "Lakshadweep", value: "Lakshadweep" },
    { label: "Puducherry", value: "Puducherry" },
  ];

  // Legal Provider Types
  const providerTypeOptions = [
    { label: "Law Firm", value: "law_firm" },
    { label: "Solo Practitioner", value: "solo_practitioner" },
    { label: "Legal Clinic", value: "legal_clinic" },
    { label: "Corporate Legal Department", value: "corporate_legal" },
    { label: "Public Interest Law Organization", value: "public_interest" },
    { label: "Government Legal Office", value: "government_legal" },
    { label: "Legal Aid Society", value: "legal_aid" },
    { label: "Arbitration Center", value: "arbitration_center" },
    { label: "Mediation Center", value: "mediation_center" },
    { label: "Legal Consultancy", value: "legal_consultancy" },
    { label: "Notary Office", value: "notary_office" },
    { label: "Court Complex", value: "court_complex" },
    { label: "Bar Association", value: "bar_association" },
    { label: "Legal Research Institute", value: "legal_research" },
    { label: "Paralegal Services", value: "paralegal_services" }
  ];

  const languageOptions = [
    { label: 'English', value: 'english' },
    { label: 'Hindi', value: 'hindi' },
    { label: 'Bengali', value: 'bengali' },
    { label: 'Telugu', value: 'telugu' },
    { label: 'Marathi', value: 'marathi' },
    { label: 'Tamil', value: 'tamil' },
    { label: 'Urdu', value: 'urdu' },
    { label: 'Gujarati', value: 'gujarati' },
    { label: 'Kannada', value: 'kannada' },
    { label: 'Odia', value: 'odia' },
    { label: 'Punjabi', value: 'punjabi' },
    { label: 'Malayalam', value: 'malayalam' },
    { label: 'Assamese', value: 'assamese' },
    { label: 'Maithili', value: 'maithili' },
    { label: 'Santali', value: 'santali' },
    { label: 'Konkani', value: 'konkani' },
    { label: 'Sindhi', value: 'sindhi' },
    { label: 'Dogri', value: 'dogri' },
    { label: 'Manipuri', value: 'manipuri' },
    { label: 'Bodo', value: 'bodo' },
    { label: 'Kashmiri', value: 'kashmiri' },
    { label: 'Other', value: 'other' }
  ];

  // Client Types for Legal Services
  const clientTypeOptions = [
    { label: "Individuals", value: "individuals" },
    { label: "Small Businesses", value: "small_businesses" },
    { label: "Corporations", value: "corporations" },
    { label: "Non-Profit Organizations", value: "nonprofits" },
    { label: "Government Agencies", value: "government" },
    { label: "Start-ups", value: "startups" },
    { label: "Real Estate Developers", value: "real_estate_developers" },
    { label: "Financial Institutions", value: "financial_institutions" },
    { label: "Insurance Companies", value: "insurance_companies" },
    { label: "Healthcare Organizations", value: "healthcare_organizations" },
    { label: "Educational Institutions", value: "educational_institutions" },
    { label: "Technology Companies", value: "technology_companies" },
    { label: "International Clients", value: "international_clients" }
  ];

  // Court Representations
  const courtRepresentationOptions = [
    { label: "Supreme Court of India", value: "supreme_court" },
    { label: "High Court", value: "high_court" },
    { label: "District Court", value: "district_court" },
    { label: "Sessions Court", value: "sessions_court" },
    { label: "Magistrate Court", value: "magistrate_court" },
    { label: "Family Court", value: "family_court" },
    { label: "Consumer Court", value: "consumer_court" },
    { label: "Labor Court", value: "labor_court" },
    { label: "Tribunal", value: "tribunal" },
    { label: "Arbitration Tribunal", value: "arbitration_tribunal" },
    { label: "Debt Recovery Tribunal", value: "debt_recovery_tribunal" },
    { label: "Income Tax Appellate Tribunal", value: "income_tax_tribunal" },
    { label: "National Green Tribunal", value: "green_tribunal" },
    { label: "Competition Commission", value: "competition_commission" },
    { label: "Securities Appellate Tribunal", value: "securities_tribunal" }
  ];

  // Case Types
  const caseTypeOptions = [
    { label: "Civil Cases", value: "civil" },
    { label: "Criminal Cases", value: "criminal" },
    { label: "Corporate Law", value: "corporate" },
    { label: "Family Disputes", value: "family" },
    { label: "Property Disputes", value: "property" },
    { label: "Labor Disputes", value: "labor" },
    { label: "Tax Matters", value: "tax" },
    { label: "Consumer Complaints", value: "consumer" },
    { label: "Intellectual Property", value: "intellectual_property" },
    { label: "Banking & Finance", value: "banking_finance" },
    { label: "Insurance Claims", value: "insurance" },
    { label: "Environmental Law", value: "environmental" },
    { label: "Immigration", value: "immigration" },
    { label: "Cyber Crimes", value: "cyber_crimes" },
    { label: "Real Estate", value: "real_estate" },
    { label: "Contract Disputes", value: "contract" },
    { label: "Tort Claims", value: "tort" },
    { label: "Constitutional Matters", value: "constitutional" },
    { label: "Administrative Law", value: "administrative" },
    { label: "Human Rights", value: "human_rights" }
  ];

  // Technology Available
  const technologyOptions = [
    { label: "Case Management System", value: "case_management_system" },
    { label: "Document Management", value: "document_management" },
    { label: "E-Filing", value: "e_filing" },
    { label: "Video Conferencing", value: "video_conferencing" },
    { label: "Legal Research Tools", value: "legal_research_tools" },
    { label: "Time Tracking Software", value: "time_tracking" },
    { label: "Billing Software", value: "billing_software" },
    { label: "Client Portal", value: "client_portal" },
    { label: "Digital Signature", value: "digital_signature" },
    { label: "Cloud Storage", value: "cloud_storage" },
    { label: "Practice Management Software", value: "practice_management" },
    { label: "Online Consultation Platform", value: "online_consultation" },
    { label: "Legal Analytics", value: "legal_analytics" },
    { label: "Automated Document Generation", value: "document_generation" },
    { label: "CRM System", value: "crm_system" }
  ];

  const paymentMethodOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Credit Card", value: "Credit Card" },
    { label: "Debit Card", value: "Debit Card" },
    { label: "UPI", value: "UPI" },
    { label: "Paytm", value: "Paytm" },
    { label: "PhonePe", value: "PhonePe" },
    { label: "Google Pay", value: "Google Pay" },
    { label: "Net Banking", value: "Net Banking" },
    { label: "NEFT/RTGS", value: "NEFT/RTGS" },
    { label: "Cheque", value: "Cheque" },
    { label: "Bank Transfer", value: "Bank Transfer" },
    { label: "EMI Options", value: "EMI" },
    { label: "Retainer Agreement", value: "Retainer" },
    { label: "Contingency Fee", value: "Contingency" },
    { label: "Hourly Billing", value: "Hourly" },
    { label: "Fixed Fee", value: "Fixed Fee" }
  ];

  const accessibilityOptions = [
    { label: "Wheelchair Access", value: "Wheelchair Access" },
    { label: "Elevator", value: "Elevator" },
    { label: "Ramp", value: "Ramp" },
    { label: "Accessible Bathroom", value: "Accessible Bathroom" },
    { label: "Handicap Parking", value: "Handicap Parking" },
    { label: "Wide Doorways", value: "Wide Doorways" },
    { label: "Ground Floor Access", value: "Ground Floor Access" },
    { label: "Sign Language Support", value: "Sign Language Support" },
    { label: "Hearing Loop System", value: "Hearing Loop System" },
    { label: "Visual Aids", value: "Visual Aids" },
    { label: "Braille Materials", value: "Braille Materials" },
    { label: "Large Print Materials", value: "Large Print Materials" },
    { label: "Audio Announcements", value: "Audio Announcements" },
    { label: "Service Animals Welcome", value: "Service Animals Welcome" },
    { label: "Accessible Seating", value: "Accessible Seating" },
    { label: "Emergency Evacuation Plan", value: "Emergency Evacuation Plan" },
    { label: "Staff Disability Training", value: "Staff Disability Training" }
  ];

  // Helper functions for dynamic inputs
  const createPhoneNumber = () => "";
  const createTag = () => "";
  const createSocialMedia = () => ({ platform: "", url: "" });
  const createEducation = () => ({ institution: "", degree: "", field: "", year: "" });
  const createCertification = () => ({ name: "", issuer: "", year: "", valid_until: "" });
  const createLocation = () => ({ address: "", city: "", state: "", postal_code: "" });
  const createOfficeHours = () => ({ day: "Monday", open: "09:00", close: "17:00" });
  const createStaffMember = () => ({ name: "", role: "", qualifications: "", contact: "" });

  // Cancel and go back
  const handleCancel = () => {
    router.back();
  };
  </script>

<template>
  <div class="provider-form-container">
    <NPageHeader 
      :title="pageTitle" 
      @back="handleCancel"
    />
    
    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <p>Loading provider data...</p>
    </div>
    
    <!-- Success message -->
    <NResult 
      v-else-if="success" 
      status="success" 
      :title="isEditMode ? 'Legal Provider Updated!' : 'Legal Provider Created!'" 
      description="Your legal provider information has been saved successfully."
    >
      <template #footer>
        <NButton type="primary" @click="router.push('/my-providers')">
          Return to My Providers
        </NButton>
      </template>
    </NResult>
    
    <!-- Error message -->
    <NResult 
      v-else-if="error" 
      status="error" 
      title="Something Went Wrong" 
      :description="error"
    >
      <template #footer>
        <NButton @click="error = null">
          Try Again
        </NButton>
      </template>
    </NResult>
    
    <!-- Form content -->
    <div v-else class="provider-form">
      <!-- Step Indicator -->
      <div class="step-indicator">
        <div class="step-progress">
          <div class="progress-bar" :style="{width: `${(currentStep / totalSteps) * 100}%`}"></div>
        </div>
        <div class="step-info">
          <div class="step-title">
            <h3>{{ stepTitles[currentStep - 1] }}</h3>
          </div>
          <div class="step-count">Step {{ currentStep }} of {{ totalSteps }}</div>
        </div>
      </div>
      
      <!-- Step 1: Basic Information -->
      <NCard v-if="currentStep === 1" title="Basic Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <!-- Profile Image Upload 
          <NFormItem label="Profile Image">
            <div class="profile-image-section">
              <div class="image-upload-area">
                <NUpload
                  :custom-request="handleImageUpload"
                  accept="image/*"
                  :disabled="uploading"
                  :show-file-list="false"
                >
                  <div class="upload-trigger">
                    <div v-if="previewUrl" class="image-preview">
                      <NAvatar 
                        :src="previewUrl" 
                        :size="120"
                        object-fit="cover"
                        fallback-src="/default-avatar.png"
                      />
                      <div class="image-overlay">
                        <span>Change Image</span>
                      </div>
                    </div>
                    <div v-else class="upload-placeholder">
                      <div class="upload-icon">📷</div>
                      <div class="upload-text">Click to upload profile image</div>
                      <div class="upload-hint">JPG, PNG up to 10MB</div>
                    </div>
                  </div>
                </NUpload>
                
                <div v-if="uploading" class="upload-progress">
                  Processing and uploading image...
                </div>
                
                <div v-if="uploadError" class="upload-error">
                  {{ uploadError }}
                </div>
                
                <div v-if="previewUrl" class="image-actions">
                  <NButton size="small" type="error" @click="clearPreview">
                    Remove Image
                  </NButton>
                </div>
              </div>
            </div>
          </NFormItem>
          -->
          <NFormItem label="Legal Provider Name" required>
            <NInput
              v-model:value="formData.provider_name"
              placeholder="Enter law firm or practitioner name"
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Provider Type">
            <NSelect
              v-model:value="formData.provider_type"
              :options="providerTypeOptions"
              placeholder="Select provider type"
              filterable
              size="large"
            />
          </NFormItem>

          <NFormItem label="Practice Areas">
            <NSelect
              v-model:value="formData.specializations"
              multiple
              :options="specializationOptions"
              placeholder="Select practice areas"
              filterable
              tag
              size="large"
            />
          </NFormItem>

          <NFormItem label="Tags">
            <NDynamicInput
              v-model:value="formData.tags"
              :on-create="createTag"
              placeholder="Add relevant tags"
            />
          </NFormItem>

          <NFormItem label="About the Practice">
            <NInput
              v-model:value="formData.additional_info"
              type="textarea"
              placeholder="Describe your legal practice, experience, and areas of expertise"
              :rows="4"
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Save as Draft" v-if="formData.status === 'draft'">
            <NSwitch v-model:value="formData.is_draft" />
            <div class="hint-text">
              Save as draft if you want to complete this profile later. Draft profiles are not visible to the public.
            </div>
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="end">
            <NButton @click="handleCancel">Cancel</NButton>
            <NButton type="primary" @click="nextStep">Next: Address Details</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 2: Address Information -->
      <NCard v-if="currentStep === 2" title="Address Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Address Line 1">
            <NInput
              v-model:value="formData.address_line1"
              placeholder="Street address, building number"
              size="large"
            />
          </NFormItem>

          <NFormItem label="Address Line 2">
            <NInput
              v-model:value="formData.address_line2"
              placeholder="Suite, floor, unit (optional)"
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Address Line 3">
            <NInput
              v-model:value="formData.address_line3"
              placeholder="Additional address information (optional)"
              size="large"
            />
          </NFormItem>

          <div class="form-row">
            <NFormItem label="City" class="form-col">
              <NInput
                v-model:value="formData.city"
                placeholder="Enter city"
                size="large"
              />
            </NFormItem>

            <NFormItem label="State" class="form-col">
              <NSelect
                v-model:value="formData.state"
                :options="stateOptions"
                placeholder="Select state"
                filterable
                size="large"
              />
            </NFormItem>

            <NFormItem label="Postal Code" class="form-col">
              <NInput
                v-model:value="formData.postal_code"
                placeholder="110001"
                size="large"
              />
            </NFormItem>
          </div>

          <!-- Address Preview -->
          <div
            class="address-preview"
            v-if="formData.address_line1 || formData.city"
          >
            <h4>📮 Office Address Preview:</h4>
            <div class="preview-address">
              <div v-if="formData.address_line1">
                {{ formData.address_line1 }}
              </div>
              <div v-if="formData.address_line2">
                {{ formData.address_line2 }}
              </div>
              <div v-if="formData.address_line3">
                {{ formData.address_line3 }}
              </div>
              <div
                v-if="formData.city || formData.state || formData.postal_code"
              >
                {{ formData.city }}{{ formData.city && (formData.state || formData.postal_code) ? ", " : "" }}
                {{ formData.state }} {{ formData.postal_code }}
              </div>
            </div>
          </div>
          
          <!-- Additional Office Locations -->
          <NFormItem label="Additional Office Locations">
            <NDynamicInput
              v-model:value="formData.additional_locations"
              :on-create="createLocation"
              #="{ value, index }"
            >
              <div class="additional-location">
                <NInput
                  v-model:value="formData.additional_locations[index].address"
                  placeholder="Office Address"
                  class="mb-2"
                />
                <div class="form-row">
                  <NInput
                    v-model:value="formData.additional_locations[index].city"
                    placeholder="City"
                  />
                  <NSelect
                    v-model:value="formData.additional_locations[index].state"
                    :options="stateOptions"
                    placeholder="State"
                    filterable
                  />
                  <NInput
                    v-model:value="formData.additional_locations[index].postal_code"
                    placeholder="110001"
                  />
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Basic Information</NButton>
            <NButton type="primary" @click="nextStep">Next: Contact Info</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 3: Contact Information -->
      <NCard v-if="currentStep === 3" title="Contact Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Phone Numbers">
            <NDynamicInput
              v-model:value="formData.phone_numbers"
              :on-create="createPhoneNumber"
              placeholder="Add phone number"
              #="{ value, index }"
            >
              <NInput
                v-model:value="formData.phone_numbers[index]"
                placeholder="+91 98765 43210"
                size="large"
              />
            </NDynamicInput>
          </NFormItem>

          <NFormItem label="Website URL">
            <NInput
              v-model:value="formData.website_url"
              placeholder="https://www.lawfirm.com"
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Social Media">
            <NDynamicInput
              v-model:value="formData.social_media"
              :on-create="createSocialMedia"
              #="{ value, index }"
            >
              <div class="social-media-item">
                <NSelect
                  v-model:value="formData.social_media[index].platform"
                  placeholder="Platform"
                  :options="[
                    { label: 'LinkedIn', value: 'LinkedIn' },
                    { label: 'Facebook', value: 'Facebook' },
                    { label: 'Twitter', value: 'Twitter' },
                    { label: 'Instagram', value: 'Instagram' },
                    { label: 'YouTube', value: 'YouTube' },
                    { label: 'WhatsApp Business', value: 'WhatsApp' },
                  ]"
                  class="platform-select"
                />
                <NInput
                  v-model:value="formData.social_media[index].url"
                  placeholder="Profile URL or Handle"
                  class="url-input"
                />
              </div>
            </NDynamicInput>
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Address Details</NButton>
            <NButton type="primary" @click="nextStep">Next: Professional Details</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 4: Professional Details -->
      <NCard v-if="currentStep === 4" title="Professional Details" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Legal Services Offered">
            <NDynamicInput
              v-model:value="formData.services"
              :on-create="createTag"
              placeholder="Add legal services"
            />
            <div class="hint-text">Enter specific legal services you provide (e.g., Divorce Proceedings, Contract Drafting, Criminal Defense)</div>
          </NFormItem>
          
          <NFormItem label="Client Types">
            <NSelect
              v-model:value="formData.client_types"
              multiple
              :options="clientTypeOptions"
              placeholder="Select client types you serve"
              filterable
              tag
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Languages Spoken">
            <NSelect
              v-model:value="formData.spoken_languages"
              multiple
              :options="languageOptions"
              placeholder="Select languages"
              filterable
              tag
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Years of Experience">
            <NInputNumber
              v-model:value="formData.years_of_experience"
              placeholder="Years of legal practice"
              size="large"
              :show-button="false"
              :min="0"
              :max="100"
            />
          </NFormItem>
          
          <NFormItem label="Legal Education">
            <NDynamicInput
              v-model:value="formData.education"
              :on-create="createEducation"
              #="{ value, index }"
            >
              <div class="education-item">
                <NInput
                  v-model:value="formData.education[index].institution"
                  placeholder="Law School/University"
                  class="mb-2"
                />
                <div class="form-row">
                  <NInput
                    v-model:value="formData.education[index].degree"
                    placeholder="Degree (e.g., LLB, LLM)"
                  />
                  <NInput
                    v-model:value="formData.education[index].field"
                    placeholder="Specialization Field"
                  />
                  <NInputNumber
                    v-model:value="formData.education[index].year"
                    placeholder="Year"
                    :show-button="false"
                    :min="1900"
                    :max="2099"
                  />
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>
          
          <NFormItem label="Bar Admissions & Certifications">
            <NDynamicInput
              v-model:value="formData.certifications"
              :on-create="createCertification"
              #="{ value, index }"
            >
              <div class="certification-item">
                <NInput
                  v-model:value="formData.certifications[index].name"
                  placeholder="Bar Council/Certification Name"
                  class="mb-2"
                />
                <div class="form-row">
                  <NInput
                    v-model:value="formData.certifications[index].issuer"
                    placeholder="Issuing Authority (e.g., State Bar Council)"
                  />
                  <NInputNumber
                    v-model:value="formData.certifications[index].year"
                    placeholder="Year"
                    :show-button="false"
                    :min="1900"
                    :max="2099"
                  />
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Contact Info</NButton>
            <NButton type="primary" @click="nextStep">Next: Practice Information</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 5: Practice Information -->
      <NCard v-if="currentStep === 5" title="Practice Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Accepting New Clients">
            <NRadioGroup v-model:value="formData.accepts_new_clients">
              <NRadio :value="true">Yes</NRadio>
              <NRadio :value="false">No</NRadio>
            </NRadioGroup>
          </NFormItem>
          
          <NFormItem label="Virtual Consultations Available">
            <NRadioGroup v-model:value="formData.virtual_consultations">
              <NRadio :value="true">Yes</NRadio>
              <NRadio :value="false">No</NRadio>
            </NRadioGroup>
          </NFormItem>
          
          <NFormItem label="Office Hours">
            <NDynamicInput
              v-model:value="formData.office_hours"
              :on-create="createOfficeHours"
              #="{ value, index }"
            >
              <div class="office-hours-item">
                <NSelect
                  v-model:value="formData.office_hours[index].day"
                  :options="[
                    { label: 'Monday', value: 'Monday' },
                    { label: 'Tuesday', value: 'Tuesday' },
                    { label: 'Wednesday', value: 'Wednesday' },
                    { label: 'Thursday', value: 'Thursday' },
                    { label: 'Friday', value: 'Friday' },
                    { label: 'Saturday', value: 'Saturday' },
                    { label: 'Sunday', value: 'Sunday' },
                  ]"
                  class="day-select"
                />
                <div class="hours-inputs">
                  <NInput
                    v-model:value="formData.office_hours[index].open"
                    placeholder="09:00"
                    class="time-input"
                  />
                  <span class="to-text">to</span>
                  <NInput
                    v-model:value="formData.office_hours[index].close"
                    placeholder="17:00"
                    class="time-input"
                  />
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>
          
          <NFormItem label="Average Response Time (hours)">
            <NInputNumber
              v-model:value="formData.average_response_time_minutes"
              placeholder="Average response time in hours"
              size="large"
              :show-button="false"
              :min="0"
              :max="168"
            />
          </NFormItem>
          
          <NFormItem label="Parking Information">
            <NInput
              v-model:value="formData.parking_info"
              type="textarea"
              placeholder="Parking availability and details for clients"
              :rows="2"
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Payment Methods Accepted">
            <NSelect
              v-model:value="formData.payment_methods"
              multiple
              :options="paymentMethodOptions"
              placeholder="Select payment methods"
              filterable
              tag
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Accessibility Features">
            <NSelect
              v-model:value="formData.accessibility_options"
              multiple
              :options="accessibilityOptions"
              placeholder="Select accessibility features"
              filterable
              tag
              size="large"
            />
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Professional Details</NButton>
            <NButton type="primary" @click="nextStep">Next: Legal Practice Details</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 6: Legal Practice Details -->
      <NCard v-if="currentStep === 6" title="Legal Practice Details" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Staff Members">
            <NDynamicInput
              v-model:value="formData.provider_staff"
              :on-create="createStaffMember"
              #="{ value, index }"
            >
              <div class="staff-member-item">
                <NInput
                  v-model:value="formData.provider_staff[index].name"
                  placeholder="Staff Member Name"
                  class="mb-2"
                />
                <div class="form-row">
                  <NInput
                    v-model:value="formData.provider_staff[index].role"
                    placeholder="Role (e.g., Partner, Associate, Paralegal)"
                  />
                  <NInput
                    v-model:value="formData.provider_staff[index].qualifications"
                    placeholder="Qualifications"
                  />
                </div>
                <NInput
                  v-model:value="formData.provider_staff[index].contact"
                  placeholder="Contact Information (optional)"
                  class="mt-2"
                />
              </div>
            </NDynamicInput>
          </NFormItem>
          
          <NFormItem label="Bar Associations & Professional Organizations">
            <NDynamicInput
              v-model:value="formData.affiliated_organizations"
              :on-create="createTag"
              placeholder="Add organization name"
            />
            <div class="hint-text">List bar councils, legal associations, and professional organizations you're affiliated with</div>
          </NFormItem>
          
          <NFormItem label="Detailed Legal Services">
            <NDynamicInput
              v-model:value="formData.services_offered"
              :on-create="createTag"
              placeholder="Add specific service"
            />
            <div class="hint-text">Detailed description of legal services (e.g., "Immigration Visa Applications", "Corporate Mergers & Acquisitions")</div>
          </NFormItem>
          
          <NFormItem label="Legal Technology Available">
            <NSelect
              v-model:value="formData.technology_available"
              multiple
              :options="technologyOptions"
              placeholder="Select available technology"
              filterable
              tag
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Awards & Accreditations">
            <NDynamicInput
              v-model:value="formData.awards_accreditations"
              :on-create="createTag"
              placeholder="Add award or accreditation"
            />
          </NFormItem>
          
          <NFormItem label="Court Representations">
            <NSelect
              v-model:value="formData.court_representations"
              multiple
              :options="courtRepresentationOptions"
              placeholder="Select courts where you can represent"
              filterable
              tag
              size="large"
            />
          </NFormItem>
          
          <NFormItem label="Pro Bono Services">
            <NDynamicInput
              v-model:value="formData.pro_bono_services"
              :on-create="createTag"
              placeholder="Add pro bono service"
            />
            <div class="hint-text">Free legal aid services you offer to eligible clients</div>
          </NFormItem>
          
          <NFormItem label="Case Types Handled">
            <NSelect
              v-model:value="formData.case_types_handled"
              multiple
              :options="caseTypeOptions"
              placeholder="Select case types you handle"
              filterable
              tag
              size="large"
            />
          </NFormItem>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Practice Information</NButton>
            <NButton type="primary" @click="nextStep">Next: Review & Submit</NButton>
          </NSpace>
        </template>
      </NCard>
      
      <!-- Step 7: Review & Submit -->
      <NCard v-if="currentStep === 7" title="Review & Submit" class="form-card">
        <div class="preview-section">
          <div class="preview-header">
            <div class="provider-header-content">
              <div v-if="previewUrl" class="provider-avatar">
                <NAvatar 
                  :src="previewUrl" 
                  :size="80"
                  object-fit="cover"
                  fallback-src="/default-avatar.png"
                />
              </div>
              <div class="provider-info">
                <h2>{{ formData.provider_name || "Legal Provider Name" }}</h2>
                <div v-if="formData.provider_type" class="provider-type">
                  {{ formData.provider_type }}
                </div>
              </div>
            </div>
            <div v-if="formData.is_draft" class="draft-badge">Draft</div>
          </div>

          <div class="preview-content">
            <div class="preview-column">
              <div v-if="formData.specializations.length > 0" class="preview-item">
                <h4>⚖️ Practice Areas</h4>
                <div class="tags-container">
                  <NTag
                    v-for="spec in formData.specializations"
                    :key="spec"
                    type="primary"
                    class="preview-tag"
                  >
                    {{ spec }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.services.length > 0" class="preview-item">
                <h4>🏛️ Legal Services</h4>
                <div class="tags-container">
                  <NTag
                    v-for="service in formData.services"
                    :key="service"
                    type="success"
                    class="preview-tag"
                  >
                    {{ service }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.client_types.length > 0" class="preview-item">
                <h4>👥 Client Types</h4>
                <div class="tags-container">
                  <NTag
                    v-for="client in formData.client_types"
                    :key="client"
                    type="warning"
                    class="preview-tag"
                  >
                    {{ client }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.spoken_languages.length > 0" class="preview-item">
                <h4>🗣️ Languages</h4>
                <div class="tags-container">
                  <NTag
                    v-for="lang in formData.spoken_languages"
                    :key="lang"
                    type="info"
                    class="preview-tag"
                  >
                    {{ lang }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.education.length > 0" class="preview-item">
                <h4>🎓 Legal Education</h4>
                <div v-for="(edu, i) in formData.education" :key="i" class="preview-detail-item">
                  <div class="detail-title">{{ edu.degree }}{{ edu.field ? ' in ' + edu.field : '' }}</div>
                  <div class="detail-subtitle">{{ edu.institution }} ({{ edu.year }})</div>
                </div>
              </div>

              <div v-if="formData.certifications.length > 0" class="preview-item">
                <h4>🏆 Bar Admissions & Certifications</h4>
                <div v-for="(cert, i) in formData.certifications" :key="i" class="preview-detail-item">
                  <div class="detail-title">{{ cert.name }}</div>
                  <div class="detail-subtitle">{{ cert.issuer }} ({{ cert.year }})</div>
                </div>
              </div>

              <div v-if="formData.court_representations.length > 0" class="preview-item">
                <h4>🏛️ Court Representations</h4>
                <div class="tags-container">
                  <NTag
                    v-for="court in formData.court_representations"
                    :key="court"
                    type="primary"
                    class="preview-tag"
                  >
                    {{ court }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.case_types_handled.length > 0" class="preview-item">
                <h4>📋 Case Types</h4>
                <div class="tags-container">
                  <NTag
                    v-for="caseType in formData.case_types_handled"
                    :key="caseType"
                    type="info"
                    class="preview-tag"
                  >
                    {{ caseType }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.years_of_experience" class="preview-item">
                <h4>⏱️ Experience</h4>
                <div class="preview-text">{{ formData.years_of_experience }} years</div>
              </div>

              <div v-if="formData.additional_info" class="preview-item">
                <h4>ℹ️ About the Practice</h4>
                <p class="preview-text">{{ formData.additional_info }}</p>
              </div>
            </div>

            <div class="preview-column">
              <div class="preview-item">
                <h4>📍 Primary Office</h4>
                <div class="preview-address" v-if="formData.address_line1 || formData.city">
                  <div v-if="formData.address_line1">{{ formData.address_line1 }}</div>
                  <div v-if="formData.address_line2">{{ formData.address_line2 }}</div>
                  <div v-if="formData.address_line3">{{ formData.address_line3 }}</div>
                  <div v-if="formData.city || formData.state || formData.postal_code">
                    {{ formData.city }}{{ formData.city && (formData.state || formData.postal_code) ? ", " : "" }}
                    {{ formData.state }} {{ formData.postal_code }}
                  </div>
                </div>
                <div v-else class="preview-empty">No address provided</div>
              </div>

              <div v-if="formData.phone_numbers.length > 0" class="preview-item">
                <h4>📞 Contact Numbers</h4>
                <div v-for="phone in formData.phone_numbers" :key="phone" class="contact-item">
                  {{ phone }}
                </div>
              </div>

              <div v-if="formData.website_url" class="preview-item">
                <h4>🌐 Website</h4>
                <div class="contact-item">{{ formData.website_url }}</div>
              </div>

              <div v-if="formData.office_hours.length > 0" class="preview-item">
                <h4>🕒 Office Hours</h4>
                <div v-for="(hours, i) in formData.office_hours" :key="i" class="contact-item">
                  <strong>{{ hours.day }}:</strong> {{ hours.open }} - {{ hours.close }}
                </div>
              </div>

              <div v-if="formData.provider_staff.length > 0" class="preview-item">
                <h4>👨‍💼 Staff Members</h4>
                <div v-for="(staff, i) in formData.provider_staff" :key="i" class="preview-detail-item">
                  <div class="detail-title">{{ staff.name }} - {{ staff.role }}</div>
                  <div v-if="staff.qualifications" class="detail-subtitle">{{ staff.qualifications }}</div>
                </div>
              </div>

              <div v-if="formData.technology_available.length > 0" class="preview-item">
                <h4>💻 Technology</h4>
                <div class="tags-container">
                  <NTag v-for="tech in formData.technology_available" :key="tech" type="info" class="preview-tag">
                    {{ tech }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.payment_methods.length > 0" class="preview-item">
                <h4>💰 Payment Methods</h4>
                <div class="tags-container">
                  <NTag v-for="method in formData.payment_methods" :key="method" type="success" class="preview-tag">
                    {{ method }}
                  </NTag>
                </div>
              </div>

              <div class="preview-item">
                <h4>⚡ Practice Status</h4>
                <div class="practice-details">
                  <div v-if="formData.accepts_new_clients !== null">
                    <strong>Accepting New Clients:</strong> 
                    {{ formData.accepts_new_clients ? "Yes" : "No" }}
                  </div>
                  <div v-if="formData.virtual_consultations !== null">
                    <strong>Virtual Consultations:</strong> 
                    {{ formData.virtual_consultations ? "Available" : "Not Available" }}
                  </div>
                  <div v-if="formData.average_response_time_minutes">
                    <strong>Response Time:</strong> {{ formData.average_response_time_minutes }} hours
                  </div>
                </div>
              </div>

              <div v-if="formData.pro_bono_services.length > 0" class="preview-item">
                <h4>🤝 Pro Bono Services</h4>
                <div v-for="service in formData.pro_bono_services" :key="service" class="contact-item">
                  {{ service }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="prevStep">Back: Legal Practice Details</NButton>
            <NButton 
              type="primary" 
              @click="saveProvider" 
              :loading="saving"
            >
              {{ isEditMode ? "Update Legal Provider" : "Create Legal Provider" }}
            </NButton>
          </NSpace>
        </template>
      </NCard>
    </div>
  </div>
</template>

<style scoped>
.provider-form-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.form-card {
  margin-bottom: 2rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.step-indicator {
  margin-bottom: 2rem;
}

.step-progress {
  height: 6px;
  background-color: #eaeaea;
  border-radius: 3px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #18a058;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.step-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.step-title h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.step-count {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
}

.form-col {
  margin-bottom: 0;
}

.address-preview {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #18a058;
}

.address-preview h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 0.9rem;
}

.preview-address {
  color: #555;
  line-height: 1.5;
}

.profile-image-section {
  margin-bottom: 1rem;
}

.image-upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-trigger {
  cursor: pointer;
}

.image-preview {
  position: relative;
  border-radius: 50%;
  overflow: hidden;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  transition: border-color 0.3s ease;
}

.upload-placeholder:hover {
  border-color: #18a058;
}

.upload-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

.upload-hint {
  font-size: 0.875rem;
  color: #6b7280;
}

.upload-progress {
  color: #18a058;
  font-size: 0.9rem;
}

.upload-error {
  color: #ef4444;
  font-size: 0.9rem;
  text-align: center;
}

.image-actions {
  display: flex;
  gap: 0.5rem;
}

.preview-section {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e0e0e6;
}

.preview-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e6;
  position: relative;
}

.provider-header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.provider-info h2 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.8rem;
}

.provider-type {
  font-size: 1rem;
  color: #666;
}

.draft-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ff9800;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;
}

.preview-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.preview-column {
  display: flex;
  flex-direction: column;
}

.preview-item {
  margin-bottom: 1.5rem;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-item h4 {
  margin: 0 0 0.75rem 0;
  color: #333;
  font-size: 1rem;
  font-weight: 600;
}

.preview-text {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.preview-tag {
  margin: 0;
}

.contact-item {
  color: #555;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.preview-empty {
  color: #999;
  font-style: italic;
}

.preview-detail-item {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
}

.preview-detail-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.detail-title {
  font-weight: 600;
  color: #333;
}

.detail-subtitle {
  color: #666;
  font-size: 0.9rem;
}

.social-media-item,
.education-item,
.certification-item,
.office-hours-item,
.staff-member-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
}

.platform-select,
.day-select {
  width: 30%;
}

.url-input,
.hours-inputs {
  flex: 1;
}

.hours-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.time-input {
  width: 45%;
}

.to-text {
  color: #666;
}

.hint-text {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.additional-location {
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.practice-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .preview-content {
    grid-template-columns: 1fr;
  }
  
  .provider-header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .social-media-item,
  .education-item,
  .certification-item,
  .office-hours-item,
  .staff-member-item {
    flex-direction: column;
  }
  
  .platform-select,
  .day-select {
    width: 100%;
  }
  
  .time-input {
    width: 40%;
  }
}
</style>